import React, { useMemo, useState } from 'react';
import { type ColumnDef } from '@tanstack/react-table';
import { Table, Badge, Alert, Button } from '@shared/components';
import { formatDateOnlyMonthYear, formatDateOnlyShort } from '@/app/utils/formatDate';
import { formatMinorAmount } from '../utils/formatScheduleCurrency';
import { buildScheduleSummaryRows } from '../utils/scheduleSummary';
import { todayDateKey, type DateKey } from '../utils/dateKey';
import type { ScheduleMeta, ScheduleRow } from '../types/schedule.types';

export const ILLUSTRATIVE_SCHEDULE_NOTICE =
    'This schedule is illustrative. Actual collection dates depend on the payment method the customer chooses: Direct Debit, Recurring Card Payments or Open Banking.';

interface SchedulePreviewTableProps {
    schedule: ScheduleRow[];
    meta: ScheduleMeta;
    numPayments: number;
    currency: string;
    /**
     * #781 — true only when the shipped anchor guard has CONFIRMED that
     * `schedule[0]` is the applied deposit row (contract §8 B6/R5). The ONLY
     * sanctioned source is `resolveAppliedDeposit(...).depositRowConfirmed`
     * (`services/appliedDeposit.ts`). Never pass `params.hasDeposit`: that is
     * a request-shape flag and is not proof about any row. Absent/false
     * renders exactly as this table rendered before #781.
     */
    depositRowConfirmed?: boolean;
    anchorDate?: DateKey;
    // False while React Query serves keepPreviousData: `schedule` then predates
    // `anchorDate`, so the summary would describe a request it does not belong to.
    showSummaryLines?: boolean;
    // When true, the instalment table collapses behind a Show/Hide toggle (default collapsed);
    // the summary card stays permanent. The read-only surface leaves it false — rows always show.
    collapsibleRows?: boolean;
    // When true, only the summary card (headline + summary lines) renders — no instalment rows
    // and no toggle. The Configure-page digest of the default schedule uses this.
    summaryOnly?: boolean;
    illustrativeDates?: boolean;
}

/**
 * Renders an elemis schedule preview (#475-B). Money is always formatted
 * from integer minor units via `formatMinorAmount` (D5/D12) — never
 * hand-rolled `/100` here. The parent (`PaymentStep`) owns loading/empty/
 * error states; this component only ever receives a resolved schedule.
 */
export const SchedulePreviewTable: React.FC<SchedulePreviewTableProps> = ({
    schedule,
    meta,
    numPayments,
    currency,
    depositRowConfirmed = false,
    anchorDate,
    showSummaryLines = true,
    collapsibleRows = false,
    summaryOnly = false,
    illustrativeDates = false,
}) => {
    const [rowsShown, setRowsShown] = useState(false);
    const columns: ColumnDef<ScheduleRow>[] = [
        {
            accessorKey: 'number',
            header: 'No.',
            enableSorting: false,
            // #781 — `row.index === 0` is only a safe way to identify
            // `schedule[0]` because `enableSorting={false}` is set BELOW, on
            // this table's own `<Table>` element, and every column here also
            // sets `enableSorting: false` independently. That combination is
            // what stops `Table` wiring `getSortedRowModel` (`Table.tsx:65`),
            // which is what keeps the row model in data order. If sorting
            // were ever enabled on this table, `row.index` would become a
            // DISPLAY index and this pill could attach to the wrong row.
            cell: (info) =>
                depositRowConfirmed && info.row.index === 0 ? (
                    <span className="inline-flex items-center gap-2">
                        {info.getValue() as number}
                        <Badge variant="info">Deposit</Badge>
                        <span className="sr-only"> taken on the date shown, not an instalment</span>
                    </span>
                ) : (
                    (info.getValue() as number)
                ),
        },
        {
            accessorKey: 'date',
            header: 'Date',
            enableSorting: false,
            // `ScheduleRow.date` is date-only; both formatters read the string and
            // never construct a timezone-shifted `Date`.
            cell: (info) => {
                const exactDate =
                    !illustrativeDates || (depositRowConfirmed && info.row.index === 0);
                return (exactDate ? formatDateOnlyShort : formatDateOnlyMonthYear)(
                    info.getValue() as string
                );
            },
        },
        {
            accessorKey: 'payment',
            header: 'Payment',
            enableSorting: false,
            cell: (info) => formatMinorAmount(info.getValue() as number, currency),
        },
        // Balance column dropped 2026-08-03 (Trello xl0xbULd). `ScheduleRow.balance` is
        // deliberately RETAINED on the type: elemis still returns it (see
        // docs/integrations/elemis-payment-schedule-contract.md section 6). Not rendered.
    ];

    // #781 — both branches read the SAME `depositRowConfirmed` prop as the
    // pill above, so the count and the row label can never disagree (D1, D3).
    const instalmentCount = numPayments - 1;
    const summary =
        depositRowConfirmed && numPayments >= 1
            ? instalmentCount === 0
                ? '1 deposit'
                : `1 deposit + ${instalmentCount} payment${instalmentCount === 1 ? '' : 's'}`
            : `${numPayments} payment${numPayments === 1 ? '' : 's'}`;

    const summaryRows = useMemo(
        () =>
            buildScheduleSummaryRows({
                schedule,
                anchorDate,
                today: todayDateKey(),
                currency,
                depositRowConfirmed,
                monthYearDates: illustrativeDates,
            }),
        [schedule, anchorDate, currency, depositRowConfirmed, illustrativeDates]
    );

    const rowsTable = (
        <Table<ScheduleRow>
            data={schedule}
            columns={columns}
            enableSorting={false}
            emptyMessage="No instalments to display for this schedule."
        />
    );

    const rowsBlock = (
        <>
            {illustrativeDates && (
                <Alert variant="warning" role="note">
                    <span>
                        <span className="font-bold">Please note:</span>{' '}
                        <span data-testid="schedule-illustrative-notice">
                            {ILLUSTRATIVE_SCHEDULE_NOTICE}
                        </span>
                    </span>
                </Alert>
            )}
            {rowsTable}
        </>
    );

    return (
        <section aria-label="Payment schedule preview" className="space-y-3">
            <div
                className="px-4 py-3 border border-mp-border rounded-lg bg-mp-background"
                data-testid="schedule-preview-panel"
            >
                <div className="flex flex-wrap items-baseline justify-between gap-x-6 gap-y-1">
                    <p className="text-xs font-medium tracking-wide text-mp-muted uppercase">
                        Total payable
                    </p>
                    <p
                        className="text-sm text-mp-muted"
                        data-testid="schedule-preview-meta"
                    >
                        {summary}
                    </p>
                </div>
                <div className="flex flex-wrap items-baseline justify-between gap-x-6 gap-y-1">
                    <p
                        className="text-2xl font-semibold text-mp-foreground tabular-nums"
                        data-testid="schedule-preview-total"
                    >
                        {formatMinorAmount(meta.total_repayable, currency)}
                    </p>
                    {collapsibleRows && !summaryOnly && (
                        <Button
                            variant="clear"
                            size="none"
                            onClick={() => setRowsShown((shown) => !shown)}
                            ariaExpanded={rowsShown}
                            ariaControls="schedule-rows"
                            className="mt-1 text-sm font-medium text-mp-link"
                        >
                            {rowsShown ? 'Hide schedule' : 'Show schedule'}
                        </Button>
                    )}
                </div>
                {showSummaryLines && summaryRows.length > 0 && (
                    <div className="pt-3 mt-3 border-t border-mp-border">
                        <table data-testid="schedule-summary" className="w-full text-sm">
                            <caption className="sr-only">Schedule summary</caption>
                            <thead className="sr-only">
                                <tr>
                                    <th scope="col">Payments</th>
                                    <th scope="col">When</th>
                                    <th scope="col">Amount</th>
                                </tr>
                            </thead>
                            <tbody>
                                {summaryRows.map((row, index) => (
                                    <tr key={index}>
                                        <td className="py-1 pr-3 text-mp-foreground whitespace-nowrap">
                                            {row.label}
                                        </td>
                                        <td className="w-full py-1 pr-3 text-mp-muted">
                                            {row.when}
                                        </td>
                                        <td className="py-1 font-medium text-right text-mp-foreground whitespace-nowrap tabular-nums">
                                            {row.amount}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
            {summaryOnly ? null : collapsibleRows ? (
                <div id="schedule-rows" className={rowsShown ? 'space-y-3' : undefined}>
                    {rowsShown && rowsBlock}
                </div>
            ) : (
                rowsBlock
            )}
        </section>
    );
};
